import { useState, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DEFAULT_TOB_LANGUAGE } from '../../services/TOBService';

const HEADER_BG = '#1a3055';
const HEADER_COLOR = '#ffffff';
const CELL_BORDER = '1px solid #d0d7e2';
// const CELL_BORDER = '1px solid #424242';
const CELL_PAD = '6px 10px';
export const FIRST_COLUMN_WIDTH = 320;

const MAX_PLANS_FOR_LARGE_FONT = 3;
const LARGE_FONT_SIZE = 13;
const REGULAR_FONT_SIZE = 11;

// The legend under the TOB in the email / PDF, per TOB language (see TOB_LANGUAGES).
const LEGENDS = {
    'English': {
        title: 'Legends:',
        items: [
            'WP - Waiting Period',
            'IP - In-patient',
            'OP - Out-patient',
            'DC - Day Case',
        ],
    },
    'Bahasa Indonesia': {
        title: 'Keterangan:',
        items: [
            'WP - Masa tunggu',
            'IP - Rawat inap',
            'OP - Rawat jalan',
            'DC - Day-case',
        ],
    },
};

const SELECTED_CELL_OUTLINE = 'inset 0 0 0 2px #673ab7';

// How many rows under a placeholder column's header are merged to hold its message (rows 2-5).
const PLACEHOLDER_BLOCK_ROWS = 4;

// Regular TOB plans key off uid, custom TOB plans off plan_id -- this table is shared by both.
const planId = plan => plan.uid ?? plan.plan_id;

export default function TOBTable({ tobPlans, tobBenefitLabelsColumn, onUpdateAreaOfCover, visibleRowNumbers, editable = false, readOnly = false, onUpdateCell, planColumnWidth, selection, onSelectCells, onAddPlaceholderTob, language = DEFAULT_TOB_LANGUAGE, fillHeight = false }) {
    const legend = LEGENDS[language] || LEGENDS[DEFAULT_TOB_LANGUAGE];
    const [editingCell, setEditingCell] = useState(null); // { planUid, rowNum }
    const [editValue, setEditValue] = useState('');
    // Where a Shift-click measures its range from.
    const [anchorRow, setAnchorRow] = useState(null);
    const inputRef = useRef();
    // Tabbing tears down the current editor to open the next one, and the resulting blur
    // would commit over the cell we just moved into and close it again.
    const suppressBlurRef = useRef(false);

    // A blank custom column starts with no rows of its own, so in editable mode the plans
    // are taken as they are instead of being filtered down to the ones that loaded a table.
    const plansWithTable = editable ? (tobPlans || []) : (tobPlans || []).filter(p => p.table);
    const labelRows = tobBenefitLabelsColumn ? Object.keys(tobBenefitLabelsColumn).map(Number).sort((a, b) => a - b) : [];

    if (!tobPlans || tobPlans.length === 0) {
        return <></>;
    }

    const maxRowNumber = Math.max(
        ...plansWithTable.map(p => {
            const keys = Object.keys(p.table || {}).map(Number);
            return keys.length > 0 ? Math.max(...keys) : 0;
        }),
        labelRows.length > 0 ? Math.max(...labelRows) : 0
    );

    const defaultRowNumbers = Array.from({ length: maxRowNumber }, (_, i) => String(i + 1));
    const rowNumbers = (visibleRowNumbers && visibleRowNumbers.length > 0)
        ? defaultRowNumbers.filter(rowNum => visibleRowNumbers.includes(rowNum))
        : defaultRowNumbers;

    if (visibleRowNumbers && visibleRowNumbers.length === 0) {
        return (
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                No TOB modules selected. Use Options to choose which modules to display.
            </Typography>
        );
    }

    // A span is measured in rendered rows, not row numbers: when a module is hidden the
    // rows inside a merge disappear, and a rowspan counting them would overflow the table.
    const visibleRowSet = new Set(rowNumbers);

    const mergeMaps = plansWithTable.map(plan => {
        const spans = {};
        const skip = new Set();

        // A placeholder (a plan with no TOB) keeps its header row on its own and merges the
        // few rows under it for its message, so nothing stretches the header; a rowspan down
        // the whole column would break across pages in the PDF output.
        if (plan.placeholder) {
            const [firstRow, ...restRows] = rowNumbers.slice(1, 1 + PLACEHOLDER_BLOCK_ROWS);
            if (firstRow) spans[firstRow] = { rowspan: restRows.length + 1 };
            restRows.forEach(rowKey => skip.add(rowKey));
            return { spans, skip };
        }

        (plan.vertical_merge_cells || []).forEach(({ start_row, end_row }) => {
            const rowsInSpan = [];
            for (let row = start_row; row <= end_row; row += 1) {
                const rowKey = String(row);
                if (visibleRowSet.has(rowKey)) rowsInSpan.push(rowKey);
            }
            if (rowsInSpan.length === 0) return;

            const [firstRow, ...restRows] = rowsInSpan;
            spans[firstRow] = { rowspan: rowsInSpan.length };
            restRows.forEach(rowKey => skip.add(rowKey));
        });
        return { spans, skip };
    });

    const isAreaOfCoverRow = (labelCell) => {
        return typeof labelCell?.cell_value === 'string'
            && /area\s*of\s*(cover|coverage)/i.test(labelCell.cell_value);
    };

    const startEdit = (planUid, rowNum, currentValue) => {
        setEditingCell({ planUid, rowNum });
        setEditValue(currentValue || '');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const sortRows = rows => [...rows].sort((a, b) => Number(a) - Number(b));

    // Clicking a formatting button blurs the editor and closes it, so the cells the toolbar
    // acts on are tracked separately from the one being typed into. A selection never
    // leaves its column: Shift/Ctrl-clicking another plan starts a new one there.
    const handleCellClick = (event, planUid, rowNum, currentValue, isEditingThisCell) => {
        if (!editable) {
            if (!isEditingThisCell) startEdit(planUid, rowNum, currentValue);
            return;
        }

        const sameColumn = selection?.planUid === planUid;

        if (event.shiftKey && sameColumn && anchorRow !== null) {
            const anchorIndex = rowNumbers.indexOf(anchorRow);
            const targetIndex = rowNumbers.indexOf(rowNum);

            if (anchorIndex !== -1 && targetIndex !== -1) {
                const [from, to] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
                onSelectCells?.({ planUid, rows: rowNumbers.slice(from, to + 1) });
                setEditingCell(null);
                return;
            }
        }

        if ((event.ctrlKey || event.metaKey) && sameColumn) {
            const rows = selection.rows.includes(rowNum)
                ? selection.rows.filter(row => row !== rowNum)
                : [...selection.rows, rowNum];

            onSelectCells?.(rows.length > 0 ? { planUid, rows: sortRows(rows) } : null);
            setAnchorRow(rowNum);
            setEditingCell(null);
            return;
        }

        setAnchorRow(rowNum);
        onSelectCells?.({ planUid, rows: [rowNum] });
        if (!isEditingThisCell) startEdit(planUid, rowNum, currentValue);
    };

    const isSelected = (planUid, rowNum) => (
        editable && !readOnly && selection?.planUid === planUid && selection.rows.includes(rowNum)
    );

    const commitEdit = () => {
        if (editingCell) {
            if (editable) {
                onUpdateCell?.(editingCell.planUid, editingCell.rowNum, editValue);
            } else {
                onUpdateAreaOfCover?.(editingCell.planUid, editingCell.rowNum, editValue);
            }
        }
        setEditingCell(null);
    };

    const getColumnTable = (planUid) => plansWithTable.find(plan => planId(plan) === planUid)?.table;

    // Rows swallowed by a merge are not rendered, so tabbing has to step over them.
    const getColumnSkippedRows = (planUid) => {
        const columnIndex = plansWithTable.findIndex(plan => planId(plan) === planUid);
        return columnIndex === -1 ? new Set() : mergeMaps[columnIndex].skip;
    };

    const moveEditToAdjacentRow = (direction) => {
        if (!editingCell) return;

        const { planUid, rowNum } = editingCell;
        const skippedRows = getColumnSkippedRows(planUid);

        let nextIndex = rowNumbers.indexOf(rowNum) + direction;
        while (nextIndex >= 0 && nextIndex < rowNumbers.length && skippedRows.has(rowNumbers[nextIndex])) {
            nextIndex += direction;
        }

        if (nextIndex < 0 || nextIndex >= rowNumbers.length) {
            commitEdit();
            return;
        }

        const nextRow = rowNumbers[nextIndex];

        suppressBlurRef.current = true;
        commitEdit();
        setAnchorRow(nextRow);
        onSelectCells?.({ planUid, rows: [nextRow] });
        startEdit(planUid, nextRow, renderCell(getColumnTable(planUid), nextRow)?.cell_value || '');
        // Queued after the focus timer startEdit just set, so the flag outlives the blur.
        setTimeout(() => { suppressBlurRef.current = false; }, 0);
    };

    const handleBlur = () => {
        if (suppressBlurRef.current) return;
        commitEdit();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
        if (e.key === 'Escape') { setEditingCell(null); }
        if (e.key === 'Tab') { e.preventDefault(); moveEditToAdjacentRow(e.shiftKey ? -1 : 1); }
    };

    const renderEditor = () => (
        <input
            ref={inputRef}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                // The cell shows through now, so the text has to take the cell's colour --
                // the default black is unreadable on the dark header rows.
                color: 'inherit',
                borderRadius: 2,
                padding: '4px 6px',
                fontSize: 11,
                fontFamily: 'inherit',
                // Taken from the cell so the editor matches whatever the Bold action and
                // the cell's own alignment have set -- inputs reset both by default.
                fontWeight: 'inherit',
                textAlign: 'inherit',
                boxSizing: 'border-box',
            }}
        />
    );

    const renderCell = (column, rowNum) => {
        if (!column) return null;
        const cellData = column[rowNum] || column[String(rowNum)];
        if (!cellData) return null;
        const cell = cellData['1'] || cellData;
        return cell;
    };

    // With a pinned column width the table takes exactly the width its columns need, so
    // they never stretch to fill the container and the surrounding Box scrolls once the
    // columns outgrow it.
    const tableWidth = planColumnWidth
        ? { width: FIRST_COLUMN_WIDTH + plansWithTable.length * planColumnWidth }
        : { width: '100%' };

    // fillHeight makes this Box the one scroller in both directions, within the height its
    // parent gives it -- otherwise the horizontal scrollbar sits at the bottom of the table
    // and is only reachable once the outer area is scrolled all the way down.
    return (
        <Box sx={fillHeight ? { overflow: 'auto', flex: 1, minHeight: 0 } : { overflowX: 'auto' }}>
            <table style={{ ...tableWidth, borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                <colgroup>
                    <col style={{ width: FIRST_COLUMN_WIDTH }} />
                    {plansWithTable.map((_, i) => (
                        <col key={i} style={planColumnWidth ? { width: planColumnWidth } : { minWidth: 160 }} />
                    ))}
                </colgroup>
                {/* <thead>
                    <tr>
                        <th
                            style={{
                                background: HEADER_BG,
                                color: HEADER_COLOR,
                                padding: CELL_PAD,
                                border: CELL_BORDER,
                                textAlign: 'left',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: 11,
                            }}
                        >
                            Benefit
                        </th>
                        {plansWithTable.map((plan, i) => (
                            <th
                                key={plan.id + i}
                                style={{
                                    background: HEADER_BG,
                                    color: HEADER_COLOR,
                                    padding: CELL_PAD,
                                    border: CELL_BORDER,
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: 11,
                                }}
                            >
                                {plan.label}
                            </th>
                        ))}
                    </tr>
                </thead> */}
                <tbody>
                    {rowNumbers.map(rowNum => {
                        const labelCell = renderCell(tobBenefitLabelsColumn, rowNum);
                        const labelValue = labelCell?.cell_value || '';
                        return (
                            <tr key={rowNum}>
                                <td
                                    style={{
                                        border: CELL_BORDER,
                                        padding: CELL_PAD,
                                        backgroundColor: labelCell?.cell_bg_color || '#f7f9fc',
                                        color: labelCell?.cell_text_color || '#000',
                                        fontWeight: labelCell?.font_weight || '600',
                                        textAlign: labelCell?.horizontal_alignment === 'general'
                                            ? 'left'
                                            : (labelCell?.horizontal_alignment || 'left'),
                                        verticalAlign: labelCell?.vertical_alignment || 'middle',
                                        fontSize: 11,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {labelValue}
                                </td>
                                {plansWithTable.map((plan, colIdx) => {
                                    const merge = mergeMaps[colIdx];
                                    if (merge.skip.has(rowNum)) {
                                        return null;
                                    }
                                    const span = merge.spans[rowNum];

                                    // A placeholder's header row is styled like the other TOBs' header,
                                    // so the column lines up with them.
                                    if (plan.placeholder && rowNum === rowNumbers[0]) {
                                        const headerCell = renderCell(plansWithTable.find(p => !p.placeholder && p.table)?.table, rowNum);
                                        return (
                                            <td
                                                key={colIdx}
                                                style={{
                                                    border: CELL_BORDER,
                                                    padding: CELL_PAD,
                                                    backgroundColor: headerCell?.cell_bg_color || HEADER_BG,
                                                    color: headerCell?.cell_text_color || HEADER_COLOR,
                                                    fontWeight: headerCell?.font_weight || '700',
                                                    textAlign: 'center',
                                                    verticalAlign: headerCell?.vertical_alignment || 'middle',
                                                    fontSize: 11,
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {plan.label}
                                            </td>
                                        );
                                    }

                                    // Rows 2-5 are merged to carry the message and Add button; the rows
                                    // below are plain blank cells. Under the header the column keeps only
                                    // its outside border -- left, right and the bottom of its last cell.
                                    // The classes keep that in the PDF HTML, which rewrites every
                                    // cell's border to a full one.
                                    if (plan.placeholder) {
                                        const isMessageRow = rowNum === rowNumbers[1];
                                        const reachesTableEnd = rowNum === rowNumbers[rowNumbers.length - 1]
                                            || (isMessageRow && rowNumbers.length <= 1 + PLACEHOLDER_BLOCK_ROWS);
                                        return (
                                            <td
                                                key={colIdx}
                                                rowSpan={span?.rowspan}
                                                className={reachesTableEnd ? 'remove-border-top-on-email' : 'remove-border-top-on-email remove-border-bottom-on-email'}
                                                style={{
                                                    borderLeft: CELL_BORDER,
                                                    borderRight: CELL_BORDER,
                                                    borderTop: 'none',
                                                    borderBottom: reachesTableEnd ? CELL_BORDER : 'none',
                                                    padding: CELL_PAD,
                                                    backgroundColor: '#fff',
                                                    textAlign: 'center',
                                                    verticalAlign: 'top',
                                                    fontSize: 11,
                                                }}
                                            >
                                                {isMessageRow && (
                                                    <>
                                                        {/* Inline styles, not sx: the PDF HTML is a DOM clone without the
                                                            page's stylesheets, and the email/PDF classes zero the cell's
                                                            top padding, so the gap under the header lives here. */}
                                                        <div style={{ color: '#757575', fontSize: 12, fontStyle: 'italic', paddingTop: 6 }}>No Available TOB</div>
                                                        {onAddPlaceholderTob && (
                                                            <Button
                                                                className="remove-in-email"
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<AddIcon />}
                                                                onClick={() => onAddPlaceholderTob(plan)}
                                                                sx={{ mt: 1, textTransform: 'none', color: '#673ab7', borderColor: '#673ab7' }}
                                                            >
                                                                Add
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </td>
                                        );
                                    }

                                    const cell = renderCell(plan.table, rowNum);
                                    const isAreaRow = isAreaOfCoverRow(labelCell);
                                    // readOnly keeps the editable layout (every column, placeholders included)
                                    // but turns off clicking into cells.
                                    const isCellEditable = !readOnly && (editable || isAreaRow);
                                    const isEditing = isCellEditable && editingCell?.planUid === planId(plan) && editingCell?.rowNum === rowNum;
                                    const cellValue = cell?.cell_value || '';

                                    return (
                                        <td
                                            key={colIdx}
                                            rowSpan={span?.rowspan}
                                            // The image-capture path (used for large tables in emails) screenshots
                                            // this table live, so the selection outline is hidden through this class
                                            // rather than the remove-in-email convention -- it can't remove the cell.
                                            className={isSelected(planId(plan), rowNum) ? 'remove-in-email-selection-outline' : undefined}
                                            style={{
                                                border: CELL_BORDER,
                                                padding: CELL_PAD,
                                                backgroundColor: cell?.cell_bg_color || '#fff',
                                                color: cell?.cell_text_color || '#000',
                                                fontWeight: cell?.font_weight || '400',
                                                textAlign: cell?.horizontal_alignment === 'general'
                                                    ? 'left'
                                                    : (cell?.horizontal_alignment || 'center'),
                                                verticalAlign: cell?.vertical_alignment || 'middle',
                                                fontSize: 11,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                cursor: isCellEditable ? 'text' : 'default',
                                                userSelect: editable && !readOnly ? 'none' : undefined,
                                                boxShadow: isSelected(planId(plan), rowNum) ? SELECTED_CELL_OUTLINE : undefined,
                                            }}
                                            onClick={event => isCellEditable && handleCellClick(event, planId(plan), rowNum, cellValue, isEditing)}
                                        >
                                            {isEditing ? renderEditor() : (
                                                // In editable mode every cell is clickable, so the hint would be on
                                                // most of the table -- it only helps where a single row stands out.
                                                cellValue || (!editable && !readOnly && isAreaRow ? <span style={{ color: '#bbb' }}>Click to edit</span> : '')
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <p className="show-in-email" style={{ fontSize: tobPlans.length <= MAX_PLANS_FOR_LARGE_FONT ? LARGE_FONT_SIZE : REGULAR_FONT_SIZE, fontStyle: 'italic', marginTop: 13, display: 'none' }}>
                <span style={{ color: '#f44336', fontWeight: 'bold' }}>{legend.title} </span><br/>
                {legend.items.map((item, i) => (
                    <span key={item}>{i > 0 && <br/>}<span>{item}</span></span>
                ))}
            </p>
        </Box>
    );
}
